<script>
import {mapActions} from "vuex";
import InfiniteAutocomplete from "./InfiniteAutocomplete.vue";

/**
 * 排出事業者・事業場選択コンポーネント
 *
 */
export default {
  name: "InfiniteEmissionAndLocationAutoComplete",
  components: {
    InfiniteAutocomplete,
  },
  props: {
    /** 事業者ID */
    supplierId: {
      type: Number,
      default: () => null,
    },
    /** 排出事業場ID */
    locationId: {
      type: Number,
      default: null,
    },
    /** 事業場コード */
    locationCode: {
      type: String,
      default: null,
    },
    /** 事業者ラベル */
    supplierLabel: {
      type: String,
      default: "排出事業者",
    },
    /** 事業場ラベル */
    locationLabel: {
      type: String,
      default: "排出事業場",
    },
    /** 事業者 必須 */
    supplierRequired: {
      type: Boolean,
      default: false,
    },
    locationRequired: {
      type: Boolean,
      default: false,
    },
    /** 事業者 非表示 */
    withoutInvisible: {
      type: Boolean,
      default: false,
    },
    /** 事業者 非活性化 */
    supplierDisabled: {
      type: Boolean,
      default: false,
    },
    /** 事業場 非活性化 */
    locationDisabled: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    supplierMd: {
      type: [Number, String],
      default: 12,
    },
    locationMd: {
      type: [Number, String],
      default: 12,
    },
    /**
     * ドロップダウンコントロールに適用するクラスを指定
     */
    classes: {
      type: String,
      default: null,
    },
    /**
     * マニフェスト区分に「一廃」が設定された排出事業者を除外するか
     * @type { Boolean }
     * @description trueを設定すると除外
     */
    withoutGeneralForm: {
      type: Boolean,
      default: () => false,
    },
    /**
     * 選択できない業者を選択可能とするか
     */
    uncontrolDisabled: {
      type: Boolean,
      default: false
    },
    onlyValidLocation: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザー操作で事業者を選択した際に事業場が1件であれば
     * 自動選択する.
     */
    locationAutoSelect: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      supplierLoading: false,
      supplierSearchKeyword: null,
      supplierTimerId: undefined,
      supplierPageNumber: 0,
      supplierPagination: {
        next_page: null,
        prev_page: null,
        is_first_page: false,
        is_last_page: false,
      },
      locationLoading: false,
      locationSearchKeyword: null,
      locationTimerId: undefined,
      locationPageNumber: 0,
      locationPagination: {
        next_page: null,
        prev_page: null,
        is_first_page: false,
        is_last_page: false,
      },
      suppliers: [],
      locations: [],
      workableId: null,
      workableLocationId: null,
      workableLocationCode: null,
      changedByOperate: false,
    };
  },
  computed: {
    /**
     * 事業場の自動選択を行うかどうか。
     * 呼び出し元にて「locationAutoSelect」にtrueが指定され、ユーザーによりドロップダウンが変更された場合
     * @return {false|boolean}
     */
    shouldSelectFirstLocation () {
      return this.locationAutoSelect && this.changedByOperate;
    },
  },
  watch: {
    /**
     * 事業者IDの変更検知
     * @param newValue
     * @param oldValue
     */
    supplierId(newValue, oldValue) {
      this.supplierSearchKeyword = null;
      this.locationSearchKeyword = null;
      this.workableId = newValue;
      if (newValue !== oldValue) {
        this.loadSupplier(this.workableId);
        this.reloadLocations();
      }
    },
    /**
     * 事業場IDの変更検知
     * @param newValue
     */
    locationId(newValue) {
      this.supplierSearchKeyword = null;
      this.locationSearchKeyword = null;
      this.workableLocationId = newValue;
    },
    /**
     * 事業場コードの変更検知
     * @param newValue
     */
    locationCode(newValue) {
      this.workableLocationCode = newValue
    },
    /**
     * 作業用事業場IDの変更検知
     * 呼び出し元に値を返却する.
     */
    workableId() {
      this.returnCurrentValues();
    },
    /**
     * 作業用事業者IDの変更検知
     */
    workableLocationId() {
      this.returnCurrentValues();
    },
  },
  created() {
    if (this.supplierId) {
      this.workableId = this.supplierId;
      this.loadSupplier(this.workableId);
    } else {
      this.fetchSupplierMembers();
    }

    if (this.locationCode) {
      this.workableLocationCode = this.locationCode;
    }

    if (this.locationId) {
      if (this.supplierId) {
        this.workableLocationId = this.locationId;
        this.reloadLocations();
      } else {
        this.workableLocationId = this.locationId;
      }
    } else {
      if (this.supplierId) {
        this.reloadLocations();
      } else {
        this.fetchLocationMembers();
      }
    }

  },
  methods: {
    ...mapActions("DriverMasterSearchEmissionMember", {
      fetchSuppliers: "index",
    }),
    ...mapActions("DriverMasterSearchEmissionWorkplace", {
      fetchLocations: "index",
    }),
    sharedCondition () {
      return {
        without_general_form: this.withoutGeneralForm,
        uncontrol_disabled: this.uncontrolDisabled,
        only_valid_location: this.onlyValidLocation
      }
    },
    /**
     * 事業者のロード
     * @param supplier_id { Number } 事業者ID
     */
    loadSupplier(supplier_id) {
      const conditions = {
        ...this.sharedCondition(),
        target_id: supplier_id,
        without_invisible: this.withoutInvisible,
        page: 1,
      };
      this.fetchSuppliers({ params: conditions }).then((response) => {
        this.suppliers = response.emissions;
        this.supplierPagination = response.pagination;
        this.supplierPageNumber = response.pagination.current_page;
      });
    },
    /**
     * 事業者のリロード処理
     */
    reloadSuppliers() {
      this.supplierSearchKeyword = null;
      this.supplierPageNumber = 0;
      this.suppliers = [];
      Object.assign(
        this.$data.supplierPagination,
        this.$options.data().supplierPagination
      );
      this.fetchSupplierMembers();
    },
    /**
     * 事業場のリロード処理
     */
    reloadLocations() {
      this.locationSearchKeyword = null;
      this.locationPageNumber = 0;
      this.locations = [];
      Object.assign(
        this.$data.locationPagination,
        this.$options.data().locationPagination
      );
      this.fetchLocationMembers();
    },
    /**
     * 事業者オプション取得アクション
     * @returns {Promise<unknown>}
     */
    fetchSupplierMembers() {
      this.supplierLoading = true;
      const conditions = {
        ...this.sharedCondition(),
        query: this.supplierSearchKeyword,
        location_id: this.workableLocationId,
        without_invisible: this.withoutInvisible,
        page: this.supplierPageNumber,
      };

      this.fetchSuppliers({ params: conditions }).then((response) => {
          this.suppliers = this.suppliers.concat(response.emissions);
          // 排出事業場が選択済で事業者リストが1件の場合は選択状態とする.
          if (this.workableLocationId && this.suppliers.length === 1) {
            this.workableId = this.suppliers[0].value;
          }
          this.supplierPagination = response.pagination;
          this.supplierPageNumber = response.pagination.current_page;
        })
        .finally(() => {
          this.supplierLoading = false;
        });
    },
    /**
     * 事業場オプション取得アクション
     */
    fetchLocationMembers() {
      this.locationLoading = true;
      const conditions = {
        ...this.sharedCondition(),
        query: this.locationSearchKeyword,
        emission_id: this.workableId,
        without_invisible: this.withoutInvisible,
      };
      this.fetchLocations({ query: conditions, page: this.locationPageNumber }).then((response) => {
        this.locations = this.locations.concat( response.emission_locations);
        if (this.shouldSelectFirstLocation && this.workableId && this.locations.length === 1) {
          this.workableLocationId = this.locations[0].value;
          this.workableLocationCode = this.locations[0].code;
        }
        this.changedByOperate = false;
        this.locationPagination = response.pagination;
        this.locationPageNumber = response.pagination.current_page;
      })
      .finally(() => {
        this.locationLoading = false;
        this.changedByOperate = false;
      });
    },
    /**
     * 事業者 チェンジイベントコールバック
     */
    supplierChangeCallback() {
      this.workableLocationId = null;
      this.workableLocationCode = null;
      this.changedByOperate = true;
      this.reloadLocations();
    },
    /**
     * 事業場 チェンジイベントコールバック
     */
    locationChangeCallback() {
      const object = { id: this.workableLocationId, location: null };
      if (this.workableLocationId) {
        object["location"] = this.locations.find( (v) => v.value === this.workableLocationId );
      }

      this.workableLocationCode = null;
      if (object["location"]) {
        this.workableLocationCode = object["location"]["code"];
      }

      if (object && object["location"]["parent_id"] !== this.workableId) {
        this.workableId = object["location"]["parent_id"];
        this.reloadSuppliers();
      }
    },
    /**
     * 事業者クリア コールバック
     */
    supplierClearCallback() {
      this.workableLocationCode = null;
      this.workableLocationId = null;
      this.workableId = null;
      this.reloadSuppliers();
    },
    /**
     * 事業場クリア コールバック
     */
    locationClearCallback() {
      this.workableLocationCode = null;
      this.workableLocationId = null;
      this.reloadLocations();
    },
    /**
     * 事業者 次ページ読込
     */
    supplierInsectCallback() {
      if (this.supplierPageNumber > 0) {
        this.supplierPageNumber++;
        this.fetchSupplierMembers();
      }
    },
    /**
     * 事業場 次ページ読込
     */
    locationInsectCallback() {
      if (this.locationPageNumber > 0) {
        this.locationPageNumber++;
        this.fetchLocationMembers();
      }
    },
    /**
     * 排出事業者 エンター　キーダウンコールバック
     * @param value { String } キーワード
     */
    supplierEnterKeydownCallback (value) {
      this.supplierPageNumber = 0;
      this.supplierSearchKeyword = value;
      this.fetchSupplierMembers();
    },
    /**
     * 事業場 エンター キーダウンコールバック
     * @param value { String } キーワード
     */
    locationEnterKeydownCallback (value) {
      this.locationPageNumber = 0;
      this.locationSearchKeyword = value;
      this.fetchLocationMembers();
    },
    /**
     * 親コンポーネントへの伝達
     */
    returnCurrentValues () {
      this.$emit("update:values", this.workableId, this.workableLocationId, this.workableLocationCode)
    },

    focusToSupplier () {
      this.$nextTick(() => {
        this.$refs.supplier.focusIn();
      });
    },
  },
};
</script>
<template>
  <div>
    <v-row>
      <v-col cols="12" :md="supplierMd">
        <validation-provider
          v-slot="{ errors }"
          :vid="`emission_id`"
          :value="workableId"
          :rules="{ required: supplierRequired }"
          :class="{ requiredMark: supplierRequired }"
          :name="supplierLabel"
        >
          <InfiniteAutocomplete
            v-model="workableId"
            ref="supplier"
            :classes="classes"
            :disabled="supplierDisabled"
            :errorMessages="errors"
            :label="supplierLabel"
            :loading="supplierLoading"
            :members="suppliers"
            :pagination="supplierPagination"
            :rounded="rounded"
            item-text="label"
            @change="supplierChangeCallback"
            @click:clear="supplierClearCallback"
            @insect="supplierInsectCallback"
            @keydown:enter="supplierEnterKeydownCallback"
          >
            <template v-slot:prepend>
              <slot name="supplier-prepend" />
            </template>
            <template v-slot:prepend-inner>
              <slot name="supplier-prepend-inner" />
            </template>
            <template v-slot:selection="{ item }">
              <slot name="supplier-selection" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:item="{ item }">
              <slot name="supplier-item" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:append>
              <slot name="supplier-append" />
            </template>
            <template v-slot:append-outer>
              <slot name="supplier-append-outer" />
            </template>
          </InfiniteAutocomplete>
        </validation-provider>
      </v-col>
      <slot name="between" />
      <v-col cols="12" :md="locationMd">
        <validation-provider
          v-slot="{ errors }"
          :vid="`emission_location_id`"
          :value="workableLocationId"
          :rules="{ required: locationRequired }"
          :class="{ requiredMark: locationRequired }"
          :name="locationLabel"
        >
          <InfiniteAutocomplete
            v-model="workableLocationId"
            ref="location"
            :classes="classes"
            :disabled="locationDisabled"
            :errorMessages="errors"
            :label="locationLabel"
            :loading="locationLoading"
            :members="locations"
            :pagination="locationPagination"
            :rounded="rounded"
            item-text="label"
            @change="locationChangeCallback"
            @click:clear="locationClearCallback"
            @insect="locationInsectCallback"
            @keydown:enter="locationEnterKeydownCallback"
          >
            <template v-slot:prepend>
              <slot name="location-prepend" />
            </template>
            <template v-slot:prepend-inner>
              <slot name="location-prepend-inner" />
            </template>
            <template v-slot:selection="{ item }">
              <slot name="location-selection" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:item="{ item }">
              <slot name="location-item" v-bind:item="item">
                {{ item.label }}
              </slot>
            </template>
            <template v-slot:append>
              <slot name="location-append" />
            </template>
            <template v-slot:append-outer>
              <slot name="location-append-outer" />
            </template>
          </InfiniteAutocomplete>
        </validation-provider>
      </v-col>
      <slot name="append"></slot>
    </v-row>
  </div>
</template>
